import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { OrderDetail } from '../../types/pendingOrdersFormTypes'
import type { PendingOrderFormData } from '../../types/pendingOrdersFormTypes'
import type { Product } from '../../types/orderFormTypes'
import { useProductSearch } from '../useProductSearch'
import { isProductInactiveInSearch } from '../../utils/productSearchUtils'
import { fetchMappedProductsBySkus } from '../../utils/fetchMappedProductsBySkus'
import {
  calculatePackageDimensions,
  tipoServicioFromPeso
} from '../../utils/packageDimensionsFromProducts'
import { calculateTotals as computeOrderLineTotals } from '../../utils/orderFormUtils'

type LineItem = OrderDetail['lineItems'][number]

interface UseQuoteShippingProductsProps {
  enabled: boolean
  orderDetail?: OrderDetail | null
  setFormData: React.Dispatch<React.SetStateAction<PendingOrderFormData>>
  onTipoServicioChange: (tipoServicio: string) => void
  clienteNeto?: boolean
}

function collectSkus(items: LineItem[], details: Record<string, any>): string[] {
  const skus: string[] = []
  items.forEach(item => {
    if (item.product_id) skus.push(String(item.product_id))
    item.components?.forEach(component => {
      if (component.sku) skus.push(String(component.sku))
    })
  })
  Object.values(details).forEach(detail => {
    if (detail?.is_combo === 1 && Array.isArray(detail.components)) {
      detail.components.forEach((component: { sku?: string }) => {
        if (component.sku) skus.push(String(component.sku))
      })
    }
  })
  return Array.from(new Set(skus.filter(Boolean)))
}

function skuKey(items: LineItem[]): string {
  return items
    .map(item => {
      const components = (item.components || []).map(component => component.sku).join('+')
      return `${item.product_id}:${components}`
    })
    .join('|')
}

export function useQuoteShippingProducts({
  enabled,
  orderDetail,
  setFormData,
  onTipoServicioChange,
  clienteNeto = false
}: UseQuoteShippingProductsProps) {
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [productDetails, setProductDetails] = useState<Record<string, any>>({})
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({})
  const [expandedCombos, setExpandedCombos] = useState<Record<string, boolean>>({})
  const seededOrderIdRef = useRef<number | null | undefined>(undefined)
  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    clearSearch
  } = useProductSearch()

  useEffect(() => {
    if (!enabled) return
    const orderId = orderDetail?.id ?? null
    if (seededOrderIdRef.current === orderId) return
    seededOrderIdRef.current = orderId
    const items = orderDetail?.lineItems?.map(item => ({
      ...item,
      components: item.components?.map(component => ({ ...component }))
    })) ?? []
    setLineItems(items)
    const expanded: Record<string, boolean> = {}
    items.forEach(item => {
      expanded[item.product_id] = true
    })
    setExpandedCombos(expanded)
  }, [enabled, orderDetail?.id, orderDetail?.lineItems])

  const itemsKey = useMemo(() => skuKey(lineItems), [lineItems])

  useEffect(() => {
    if (!enabled) return
    const skus = collectSkus(lineItems, {})
    if (!skus.length) {
      setProductDetails({})
      return
    }

    let cancelled = false
    skus.forEach(sku => {
      setLoadingDetails(prev => ({ ...prev, [sku]: true }))
    })

    const load = async () => {
      const firstPass = await fetchMappedProductsBySkus(skus)
      const withComponents = collectSkus(lineItems, firstPass)
      const missingComponents = withComponents.filter(sku => !firstPass[sku] && !firstPass[sku.replace(/^0+/, '')])
      const secondPass = missingComponents.length
        ? await fetchMappedProductsBySkus(missingComponents)
        : {}
      if (cancelled) return
      const merged = { ...firstPass, ...secondPass }
      lineItems.forEach(item => {
        const id = String(item.product_id || '')
        const cleaned = id.replace(/^0+/, '')
        if (id && !merged[id] && cleaned && merged[cleaned]) merged[id] = merged[cleaned]
      })
      setProductDetails(prev => ({ ...prev, ...merged }))
      setLineItems(prev => {
        let changed = false
        const next = prev.map(item => {
          const detail = merged[item.product_id]
          if (!detail || detail.is_combo !== 1 || !Array.isArray(detail.components) || !detail.components.length) {
            return item
          }
          if (item.components?.length) return item
          changed = true
          return {
            ...item,
            is_combo: 1 as const,
            components: detail.components.map((component: any) => ({
              sku: String(component.sku),
              quantity: Number(component.quantity) || 1,
              name: component.name || '',
              price: String(component.price ?? '0'),
              tax_rate: String(component.tax_rate ?? '0.19')
            }))
          }
        })
        return changed ? next : prev
      })
      const done: Record<string, boolean> = {}
      withComponents.forEach(sku => {
        done[sku] = false
      })
      setLoadingDetails(prev => ({ ...prev, ...done }))
    }

    void load()
    return () => {
      cancelled = true
    }
    // itemsKey ya representa los SKU; la cantidad no debe volver a consultar productos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, itemsKey])

  useEffect(() => {
    if (!enabled) return
    const detailsReady = lineItems.every(item => Boolean(productDetails[item.product_id]))
    const dims = !lineItems.length || !detailsReady
      ? { largo: 0, ancho: 0, altura: 0, peso: 0 }
      : calculatePackageDimensions(lineItems, productDetails)
    setFormData(prev => ({
      ...prev,
      largo: dims.largo,
      ancho: dims.ancho,
      altura: dims.altura,
      peso: dims.peso
    }))
    onTipoServicioChange(tipoServicioFromPeso(Number(dims.peso) || 0))
  }, [enabled, lineItems, productDetails, setFormData, onTipoServicioChange])

  const handleSelectProduct = useCallback((selectedProduct: Product) => {
    if (isProductInactiveInSearch(selectedProduct)) {
      toast.error('Este producto está inactivo y no se puede agregar', { icon: '⚠️' })
      return
    }
    const cleanSku = selectedProduct.sku?.toString().replace(/^0+/, '') || selectedProduct.product_id
    setLineItems(prev => {
      const index = prev.findIndex(item => item.product_id === cleanSku || item.product_id === selectedProduct.product_id)
      if (index >= 0) {
        return prev.map((item, itemIndex) => (
          itemIndex === index ? { ...item, quantity: (item.quantity || 0) + 1 } : item
        ))
      }
      return [
        ...prev,
        {
          product_id: cleanSku,
          product_name: selectedProduct.name || '',
          quantity: 1,
          price: String(selectedProduct.price || 0),
          is_combo: selectedProduct.is_combo ? 1 : 0,
          components: selectedProduct.components?.map(component => ({
            sku: component.sku,
            quantity: component.quantity,
            name: component.name,
            price: String(component.price),
            tax_rate: String(component.tax_rate)
          }))
        }
      ]
    })
    if (selectedProduct.is_combo) {
      setExpandedCombos(prev => ({ ...prev, [cleanSku]: true }))
    }
    clearSearch()
  }, [clearSearch])

  const handleQuantityChange = useCallback((index: number, quantity: number) => {
    const nextQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    setLineItems(prev => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, quantity: nextQuantity } : item
    )))
  }, [])

  const handleRemoveProduct = useCallback((index: number) => {
    setLineItems(prev => prev.filter((_, itemIndex) => itemIndex !== index))
  }, [])

  const calculateTotals = useCallback((items: any[] = []) => {
    return computeOrderLineTotals(items, productDetails, '0', clienteNeto)
  }, [productDetails, clienteNeto])

  const toggleCombo = useCallback((productId: string) => {
    setExpandedCombos(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }))
  }, [])

  return {
    lineItems,
    productDetails,
    loadingDetails,
    expandedCombos,
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    handleSelectProduct,
    handleQuantityChange,
    handleRemoveProduct,
    toggleCombo,
    calculateTotals
  }
}
